import { useCallback, useMemo, useState, type FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import TextArea from '@wix/da-ds/pkg/formControls/dsPresets/TextArea';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ImageIcon from '@wix/da-ds/pkg/Icons/24x24/AddImage';
import MediaSelector from '@wix/da-shared-react/pkg/MediaPopup/MediaSelector';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { PapiDeviation } from '@wix/da-papi-types';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import ConfirmationModal from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';
import {
  getVideoCreatorPrompt,
  getVideoCreatorReferenceDeviation,
  getVideoCreatorErrorCode,
} from '../../../../selectors/studio';
import {
  updateVideoCreatorPrompt,
  updateVideoCreatorReferenceDeviation,
} from '../../../../actions/studio/videoCreator';
import { VideoCreatorErrorCode } from '../../../../types/store';
import { getBabelKeyFromErrorCode } from '../../errorHandling';

import s from './Prompt.scss';
import sControl from '../VideoCreatorControls.scss';

const MAX_PROMPT_LENGTH = 400;

export interface Props {}

const Prompt: FC<Props> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const prompt = useSelector(getVideoCreatorPrompt);
  const errorCode = useSelector(getVideoCreatorErrorCode);
  const hasError =
    errorCode !== VideoCreatorErrorCode.NO_ERROR &&
    errorCode !== VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE;

  const referenceDeviation = useSelector(getVideoCreatorReferenceDeviation);
  const [isMediaSelectorVisible, setIsMediaSelectorVisible] = useState(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);

  const referenceDeviationThumb = referenceDeviation
    ? DeviationMediaService.getSmallestThumb(referenceDeviation)
    : undefined;

  const errorMessage = useMemo(() => {
    if (errorCode === VideoCreatorErrorCode.NO_ERROR) {
      return null;
    }
    const key = getBabelKeyFromErrorCode(errorCode);
    if (key) {
      return t(key);
    } else {
      return null;
    }
  }, [t, errorCode]);

  const showMediaSelector = useCallback(() => {
    setIsMediaSelectorVisible(true);
  }, []);

  const hideMediaSelector = useCallback(() => {
    setIsMediaSelectorVisible(false);
  }, []);

  const showConfirmationModal = useCallback(() => {
    setIsConfirmationModalVisible(true);
  }, []);

  const hideConfirmationModal = useCallback(() => {
    setIsConfirmationModalVisible(false);
  }, []);

  const handleConfirm = useCallback(() => {
    hideConfirmationModal();
    showMediaSelector();
  }, [hideConfirmationModal, showMediaSelector]);

  const handleImageSelect = useCallback(
    (selectedDeviations: PapiDeviation[]) => {
      const selectedDeviation = selectedDeviations[0];
      if (selectedDeviation) {
        dispatch(updateVideoCreatorReferenceDeviation(selectedDeviation));
      }
      hideMediaSelector();
    },
    [hideMediaSelector, dispatch]
  );

  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(updateVideoCreatorPrompt(e.target.value));
    },
    [dispatch]
  );

  return (
    <div className={cn(s['root'], hasError && s['has-error'])}>
      <label
        className={sControl['control-label']}
        htmlFor="video-creator-prompt"
      >
        {t('studio.videoCreator.label.prompt')}
      </label>
      <div className={s['content']}>
        <TextArea
          className={s['prompt-area']}
          wrapperClassName={s['prompt-area-wrapper']}
          id={'video-creator-prompt'}
          label={t('studio.videoCreator.label.prompt')}
          maxLength={MAX_PROMPT_LENGTH}
          isLabelHidden={true}
          placeholder={t('studio.videoCreator.promptPlaceholder')}
          value={prompt}
          noBottomSpacing={true}
          showCounter={false}
          onChange={handlePromptChange}
        />
        <div className={s['file-upload-area']}>
          <button
            id={'add-image-button'}
            className={s['add-image-button']}
            onClick={showConfirmationModal}
          >
            <div className={s['add-image-icon']}>
              {referenceDeviationThumb ? (
                <img
                  className={s['thumb-image']}
                  src={referenceDeviationThumb.src}
                  alt=""
                />
              ) : (
                <ImageIcon size={IconSize.MEDIUM} />
              )}
            </div>
            <label className={s['add-image-label']} htmlFor="add-image-button">
              {t('studio.videoCreator.label.upload')}
            </label>
          </button>
        </div>
        {hasError && errorMessage && (
          <div className={s['error-message']}>{errorMessage}</div>
        )}
      </div>
      <ConfirmationModal
        isOpen={isConfirmationModalVisible}
        title={t('studio.videoCreator.imageConfirm.title')}
        text={t('studio.videoCreator.imageConfirm.text')}
        confirmBtnLabel={t('studio.videoCreator.imageConfirm.confirm')}
        cancelBtnLabel={t('studio.videoCreator.imageConfirm.cancel')}
        onConfirm={handleConfirm}
        onCancel={hideConfirmationModal}
        onRequestClose={hideConfirmationModal}
        className={s['confirmation-modal']}
      />
      {isMediaSelectorVisible && (
        <MediaSelector
          mediaSourceTypes={[MediaSourceType.GALLERY, MediaSourceType.STASH]}
          allowedDeviationTypes={[DeviationTypes.IMAGE]}
          allowGifs={false}
          allowPremium={false}
          allowMature={false}
          onClose={hideMediaSelector}
          onSelect={handleImageSelect}
        />
      )}
    </div>
  );
};

Prompt.displayName = 'Prompt';
export default Prompt;
